<template>
  <div>
    <p class="text-muted">
      Default packages are applied automatically to every new onboarding whose account matches the
      package selectors; manual packages are added from the onboarding detail page.
    </p>

    <b-row class="mb-1">
      <b-col cols="12" md="4" class="mb-50 mb-md-0">
        <b-form-input v-model="filter" placeholder="Search..." />
      </b-col>
      <b-col cols="12" md="8" class="d-flex justify-content-md-end">
        <b-button variant="outline-primary" class="mr-50" @click="openPreview()">
          <feather-icon icon="EyeIcon" class="mr-25" /> Preview assembled form
        </b-button>
        <b-button variant="success" @click="openCreate">
          New package
        </b-button>
      </b-col>
    </b-row>

    <b-table
      striped
      hover
      responsive
      class="builder-table"
      :items="packages"
      :filter="filter"
      :filter-included-fields="['Name', 'Code', 'Description']"
      :fields="fields"
      :current-page="currentPage"
      :per-page="perPage"
      show-empty
      empty-text="No onboarding packages defined yet."
      @filtered="onFiltered"
    >
      <template #cell(Name)="{ item }">
        <strong>{{ item.Name }}</strong>
        <small v-if="item.Description" class="text-muted d-block">{{ item.Description }}</small>
      </template>

      <template #cell(Code)="{ value }">
        <code>{{ value }}</code>
      </template>

      <template #cell(IsDefault)="{ item }">
        <template v-if="item.IsDefault">
          <b-badge variant="light-success" class="mb-25">Default</b-badge>
          <div class="selector-badges">
            <b-badge v-for="t in item.ClientTypes || []" :key="'c' + t" variant="light-primary" class="mr-25">{{ t }}</b-badge>
            <span v-if="!(item.ClientTypes || []).length" class="text-muted mr-25">any client type</span>
          </div>
          <div class="selector-badges">
            <b-badge v-for="t in item.AccountTypes || []" :key="'a' + t" variant="light-info" class="mr-25">{{ t }}</b-badge>
            <span v-if="!(item.AccountTypes || []).length" class="text-muted mr-25">any account type</span>
          </div>
          <div class="selector-badges">
            <b-badge v-for="t in item.ClientCategories || []" :key="'k' + t" variant="light-warning" class="mr-25">{{ t }}</b-badge>
            <span v-if="!(item.ClientCategories || []).length" class="text-muted mr-25">any category</span>
          </div>
        </template>
        <span v-else class="text-muted">Manual</span>
      </template>

      <template #cell(items)="{ item }">
        <span>{{ summary(item.items) }}</span>
      </template>

      <template #cell(AppliedCount)="{ value }">
        <span v-if="value">{{ value }}</span>
        <span v-else class="text-muted">0</span>
      </template>

      <template #cell(actions)="{ item }">
        <b-button size="sm" variant="flat-primary" class="btn-icon" title="Edit" @click="openEdit(item)">
          <feather-icon icon="EditIcon" />
        </b-button>
        <b-button size="sm" variant="flat-secondary" class="btn-icon" title="Duplicate" @click="confirmDuplicate(item)">
          <feather-icon icon="CopyIcon" />
        </b-button>
        <b-button size="sm" variant="flat-info" class="btn-icon" title="Preview" @click="openPreview(item)">
          <feather-icon icon="EyeIcon" />
        </b-button>
        <b-button size="sm" variant="flat-danger" class="btn-icon" title="Delete" @click="confirmDelete(item)">
          <feather-icon icon="TrashIcon" />
        </b-button>
      </template>
    </b-table>

    <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" />

    <package-editor-modal ref="editor" :data="data" @saved="$emit('refresh')" @refresh="$emit('refresh')" />
    <form-preview-modal ref="preview" :data="data" />

    <!-- Delete Modal -->
    <b-modal
      v-model="showDeleteModal"
      title="Delete Package"
      ok-title="Delete"
      ok-variant="danger"
      :ok-disabled="deleteLoading"
      no-close-on-backdrop
      @ok="handleDelete"
    >
      <b-overlay :show="deleteLoading">
        <p>
          Delete <strong>{{ deleteTarget ? deleteTarget.Name : "" }}</strong>?
          It will no longer be applied to new onboardings.
        </p>
        <b-form-group label="Apply this change to">
          <b-form-radio-group v-model="deleteApplyToExisting" stacked>
            <b-form-radio :value="false">
              New onboardings only
              <small class="text-muted d-block">
                Onboardings that already have this package keep its questions, notices and document requests.
              </small>
            </b-form-radio>
            <b-form-radio :value="true">
              Also the {{ deleteTarget ? (deleteTarget.AppliedCount || 0) : 0 }} existing onboarding(s) that have this package
              <small class="text-muted d-block">
                Withdraws the package's items from those onboardings.
                Documents clients have already uploaded are kept.
              </small>
            </b-form-radio>
          </b-form-radio-group>
        </b-form-group>
      </b-overlay>
    </b-modal>
  </div>
</template>

<script>
import {
  BRow, BCol, BButton, BTable, BPagination, BBadge, BFormInput, BFormGroup,
  BFormRadio, BFormRadioGroup, BModal, BOverlay,
} from 'bootstrap-vue'
import store from '@/store'
import PackageEditorModal from './PackageEditorModal.vue'
import FormPreviewModal from './FormPreviewModal.vue'
import { apiError, plural } from './builderHelpers'

export default {
  name: 'PackagesTab',

  components: {
    BRow, BCol, BButton, BTable, BPagination, BBadge, BFormInput, BFormGroup,
    BFormRadio, BFormRadioGroup, BModal, BOverlay, PackageEditorModal, FormPreviewModal,
  },

  props: {
    data: { type: Object, required: true },
  },

  data() {
    return {
      filter: '',
      currentPage: 1,
      perPage: 15,
      totalRows: 0,
      showDeleteModal: false,
      deleteLoading: false,
      deleteTarget: null,
      deleteApplyToExisting: false,
      // id of a freshly duplicated package to open once the catalogue has been reloaded
      pendingOpenId: null,
      fields: [
        { key: 'Name', label: 'Name', sortable: true },
        { key: 'Code', label: 'Code', sortable: true },
        { key: 'IsDefault', label: 'Default / selectors' },
        { key: 'items', label: 'Items' },
        { key: 'AppliedCount', label: 'Applied to', class: 'text-center', sortable: true },
        { key: 'actions', label: 'Actions' },
      ],
    }
  },

  computed: {
    packages() {
      return this.data.packages || []
    },
  },

  watch: {
    packages: {
      immediate: true,
      handler(list) {
        this.totalRows = list.length
        if (this.pendingOpenId) {
          const pkg = list.find(p => p.id === this.pendingOpenId)
          if (pkg) {
            this.pendingOpenId = null
            this.$refs.editor.open(pkg)
          }
        }
      },
    },
  },

  methods: {
    onFiltered(items) {
      this.totalRows = items.length
      this.currentPage = 1
    },

    summary(items) {
      const list = items || []
      const count = kind => list.filter(i => i.ItemKind === kind).length
      return [
        plural(count('Question'), 'question'),
        plural(count('Notice'), 'notice'),
        plural(count('Upload'), 'upload'),
      ].join(' · ')
    },

    openCreate() {
      this.$refs.editor.open(null)
    },

    openEdit(pkg) {
      this.$refs.editor.open(pkg)
    },

    // Package preview: selector mode, preset to something the package applies to.
    openPreview(pkg) {
      if (!pkg) {
        this.$refs.preview.open({})
        return
      }
      const first = (list, fallback) => ((list && list.length) ? list[0] : (fallback && fallback[0]) || null)
      this.$refs.preview.open({
        clientType: first(pkg.IsDefault ? pkg.ClientTypes : null, this.data.clientTypes),
        accountType: first(pkg.IsDefault ? pkg.AccountTypes : null, this.data.accountTypes),
        clientCategory: first(pkg.IsDefault ? pkg.ClientCategories : null, this.data.clientCategories),
        packageIds: pkg.IsDefault ? [] : [pkg.id],
      })
    },

    confirmDuplicate(pkg) {
      this.$bvModal.msgBoxConfirm(`Duplicate "${pkg.Name}"? The copy is created as a manual package and opened for editing.`, {
        title: 'Duplicate package',
        okVariant: 'primary',
        okTitle: 'Duplicate',
        centered: true,
      }).then(ok => {
        if (!ok) return
        store.dispatch('settings/duplicateOnboardingPackage', { id: pkg.id })
          .then(res => {
            const copy = res.data.package
            this.$bvToast.toast(res.data.message || 'Package duplicated.', { variant: 'success', title: 'Success', solid: true })
            this.pendingOpenId = copy && copy.id ? copy.id : null
            this.$emit('refresh')
          })
          .catch(err => {
            this.$bvToast.toast(apiError(err), { variant: 'danger', title: 'Error', solid: true })
          })
      })
    },

    confirmDelete(pkg) {
      this.deleteTarget = pkg
      this.deleteApplyToExisting = false
      this.showDeleteModal = true
    },

    handleDelete(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.deleteLoading = true
      store.dispatch('settings/deleteOnboardingPackage', {
        id: this.deleteTarget.id,
        applyToExisting: this.deleteApplyToExisting,
      })
        .then(res => {
          const affected = res.data.affectedOnboardings
          const suffix = affected ? ` ${affected} existing onboarding(s) updated.` : ''
          this.$bvToast.toast((res.data.message || 'Package deleted.') + suffix, { variant: 'success', title: 'Deleted', solid: true })
          this.showDeleteModal = false
          this.$emit('refresh')
        })
        .catch(err => {
          this.$bvToast.toast(apiError(err), { variant: 'danger', title: 'Error', solid: true })
        })
        .finally(() => { this.deleteLoading = false })
    },
  },
}
</script>

<style lang="scss" scoped>
.selector-badges {
  font-size: 0.8rem;
  line-height: 1.6;
}
</style>
